<template>
  <div ref="form" class="py-8">
    <h3 class="body-1-bold">{{ i18n(translations.title) }}</h3>
    <p>{{ i18n(translations.info) }}</p>
    <RevForm
      :id="formId"
      data-test="formOnHoldReasons"
      :initial-values
      :validate
      @submit="handleSubmit"
      @visible-errors-change="scrollToErrorMessage"
    >
      <template #default="{ values, errors }">
        <div class="mb-8 mt-32 flex items-center">
          <component :is="categoryIcon" />
          <p class="body-1-bold ml-8">
            {{ reasonsGroup.label }}
          </p>
        </div>

        <RevCheckbox
          v-for="(reason, index) in reasonsGroup.reasons"
          :id="reason.id"
          :key="reason.id"
          v-model="values.reasons"
          class="text-static-default-low mt-16"
          :data-qa="`${reason.id}-option-${index}`"
          :value="reason.id"
        >
          <template #label>
            {{ reason.label }}
          </template>
        </RevCheckbox>

        <p v-if="errors.reasons" class="text-static-danger-hi body-2 mt-16">
          {{ errors.reasons }}
        </p>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { BuybackOrderDetailsSuspensionReasons } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { IconAvatar } from '@ds/icons/IconAvatar'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'
import { IconPackage } from '@ds/icons/IconPackage'

import { ON_HOLD_STEPS } from '../../constants'

import translations from './SuspendReasonsView.translations'

const props = withDefaults(
  defineProps<{
    step?: string
    reasonsGroup?: BuybackOrderDetailsSuspensionReasons
  }>(),
  {
    step: '',
    reasonsGroup: () => ({ id: '', label: '', reasons: [] }),
  },
)

const emit = defineEmits(['submit'])

const i18n = useI18n()

const form = ref<HTMLElement | null>(null)

const initialValues = ref({
  reasons: [],
})

const CATEGORY_ICONS = {
  [ON_HOLD_STEPS.CUSTOMER_ACTION]: IconAvatar,
  [ON_HOLD_STEPS.TRANSPORT_ISSUE]: IconPackage,
  [ON_HOLD_STEPS.REFUSE_ITEM]: IconCrossInCircle,
}

const formId = computed(() => props.step)

const categoryIcon = computed(() => CATEGORY_ICONS[props.step])

const validate = computed(() => {
  return makeValidate<typeof initialValues.value>({
    reasons: [required(i18n(translations.requiredErrorMessage))],
  })
})

const handleSubmit = (value: { reasons: string[] }) => {
  emit('submit', value.reasons)
}

const scrollToErrorMessage = () => {
  form.value?.scrollIntoView(false)
}
</script>
